import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import DashboardModal from '@/components/dashboard-modal';
import { useToast } from '@/hooks/errors/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Hash } from 'lucide-react';
import { cn } from '@/lib/utils';
import { pageTypeOptions, PageType } from '../new-page-smart-detection';
import { OpenAIThreadsService } from '@/hooks/ai-agents/services/openai/threads.service';
import { useAIOperationWrapper } from '@/hooks/ai-usage/useAIOperationWrapper';

interface QuickCreatePageModalProps {
  isOpen: boolean;
  onClose: () => void;
  workbookId?: string; // Optional - determines if project page (has ID) or private page (undefined)
  onPageCreated?: (pageId: string) => void;
  onGenerateWithAI?: (title: string) => void; // New prop for opening advanced modal with title
}

const QuickCreatePageModal: React.FC<QuickCreatePageModalProps> = ({
  isOpen,
  onClose,
  workbookId,
  onPageCreated,
  onGenerateWithAI,
}) => {
  const [title, setTitle] = useState('');
  const [pageType, setPageType] = useState<PageType | ''>('');
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { executeAIOperation } = useAIOperationWrapper();

  // Handle Generate with AI button click
  const handleGenerateWithAI = () => {
    console.log('[QuickCreatePageModal] Generate Content clicked, title:', title);
    if (onGenerateWithAI) {
      console.log('[QuickCreatePageModal] Calling onGenerateWithAI with title:', title);
      onGenerateWithAI(title);
    } else {
      console.warn('[QuickCreatePageModal] onGenerateWithAI prop not provided');
    }
  };

  const handleQuickCreate = async () => {
    setIsCreating(true);
    
    try {
      // Get the current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('You must be logged in to create documents');
      }

      // 🆕 UNIVERSAL FLOW: Create dedicated OpenAI thread for EVERY page
      console.log('[QuickCreatePageModal] Creating dedicated OpenAI thread for page...');
      const threadId = await OpenAIThreadsService.createOpenAIThread(executeAIOperation);
      console.log('[QuickCreatePageModal] Thread created:', threadId);

      // Create page with dedicated thread
      const { data, error } = await supabase
        .from('workbook_page_content')
        .insert({
          workbook_id: workbookId || null, // Project page or private page
          title: title.trim() || 'Untitled Document',
          content: '', // Empty content for blank page
          initial_request: '',
          user_id: user.id,
          openai_thread_id: threadId, // ✅ UNIVERSAL: Every page gets its own thread
          type: null // Quick create defaults to null type
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating blank page:', error);
        throw error;
      }
      
      // Invalidate queries to refresh the page list
      if (workbookId) {
        // Project page
        queryClient.invalidateQueries({ queryKey: ['project-navigation', workbookId] });
        queryClient.invalidateQueries({ queryKey: ['workbook-pages', workbookId] });
      } else {
        // Private page
        queryClient.invalidateQueries({ queryKey: ['private-pages'] });
      }
      
      toast({
        title: "Document Created!",
        description: "Your blank document has been created successfully.",
      });
      
      // Notify parent about page creation for auto-navigation
      onPageCreated?.(data.id);
      
      // Reset form and close
      setTitle('');
      onClose();
      
    } catch (error) {
      console.error('Error creating blank page:', error);
      toast({
        title: "Creation Failed",
        description: "Failed to create document. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setPageType('');
    onClose();
  };

  // Calculate if form is valid
  const isFormValid = true; // Always allow document creation (empty title defaults to "Untitled Document")
  const titleCharacterCount = title.length;

  return (
    <DashboardModal
      isOpen={isOpen}
      onClose={handleClose}
      title={workbookId ? "📄 Create New Document" : "📄 Create New Private Document"}
      size="md"
      accentColor="purple"
      hasUnsavedChanges={title.length > 0}
    >
      <div className="space-y-6">
        {/* Title Input */}
        <div className="space-y-2">
          <Label htmlFor="title" className="text-sm font-medium flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Document Title
          </Label>
          <Input
            id="title"
            placeholder="e.g., Marketing Strategy, User Research Summary, Product Launch Plan..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={isCreating}
            maxLength={100}
          />
          {title.length < 10 && title.length > 0 && (
            <p className="text-xs text-amber-600">3 more characters needed</p>
          )}
          <div className="flex justify-between items-center text-xs">
            <div className="text-gray-500">
              {title.length === 0 ? 'Start typing your document title...' : 
               title.length < 10 ? 'Keep going...' : 
               'Perfect! Ready to create'}
            </div>
            <div className="text-gray-400 font-mono">
              {title.length}/100
            </div>
          </div>
        </div>

        {/* Page Type */}
        <div className="space-y-2">
          <Label className="text-sm font-medium flex items-center gap-2">
            <Hash className="h-4 w-4" />
            What type of document is this?
          </Label>
          <Select value={pageType} onValueChange={(value) => setPageType(value as PageType)}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Choose the best type for your page..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="article">📄 Article</SelectItem>
              <SelectItem value="guide">📖 Guide</SelectItem>
              <SelectItem value="tutorial">🎯 Tutorial</SelectItem>
              <SelectItem value="overview">🌐 Overview</SelectItem>
              <SelectItem value="analysis">📊 Analysis</SelectItem>
              <SelectItem value="report">📈 Report</SelectItem>
              <SelectItem value="documentation">📚 Documentation</SelectItem>
              <SelectItem value="plan">📋 Plan</SelectItem>
              <SelectItem value="strategy">🎲 Strategy</SelectItem>
              <SelectItem value="summary">📝 Summary</SelectItem>
              <SelectItem value="other">✨ Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Inline Footer Buttons */}
        <div className="flex justify-between w-full pt-4">
          <Button 
            variant="outline" 
            onClick={handleClose}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <div className="flex gap-3">
            {/* Generate Content Button */}
            <Button 
              onClick={handleGenerateWithAI}
              disabled={isCreating}
              variant="outline"
              className="border-purple-500 text-purple-600 hover:bg-purple-50 dark:text-purple-400 dark:border-purple-400 dark:hover:bg-purple-950"
            >
              Generate Content
            </Button>
            {/* Create Page Button */}
            <Button 
              onClick={handleQuickCreate}
              disabled={!isFormValid || isCreating}
              className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white border-0"
            >
              {isCreating ? 'Creating...' : 'Create Document'}
            </Button>
          </div>
        </div>
      </div>
    </DashboardModal>
  );
};

export default QuickCreatePageModal; 